import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UflaUser } from './ufla-user';
import { OrganisationsTyp } from 'src/app/domain/model/organisationsTyp';
import { UserRoles } from '../../domain/model/roles.enum';
import { HttpClient } from '@angular/common/http';

@Injectable({
    providedIn: 'root',
})
export class AuthenticationService {
    private httpClient = inject(HttpClient);
    private logoutUrl = environment.API_URL + '/auth/logout';

    private loggedInUser$ = new BehaviorSubject<UflaUser | null>(null);

    public get isAuthenticated(): boolean {
        return !!this.user;
    }

    public async isAuthenticatedTryRespawnSession(): Promise<boolean> {
        if (!this.isAuthenticated) {
            const user = await firstValueFrom(this.httpClient.get<UflaUser | any>(environment.API_URL + '/profile'));
            this.setPrincipal(user);
        }
        return this.isAuthenticated;
    }

    // Subject to receive events about changed UflaUser, e.g. status bar
    public get user$(): Observable<UflaUser> {
        return this.loggedInUser$.asObservable();
    }

    public get user(): UflaUser {
        return this.loggedInUser$.getValue();
    }

    public setPrincipal(user: UflaUser) {
        this.loggedInUser$.next(user);
    }

    public clear() {
        this.loggedInUser$.next(null);
    }

    public containsValidRoleForThisApplication(): boolean {
        if (!this.isAuthenticated) {
            return true; // no session, so we don't know yet.
        }
        return this.checkRole([
            UserRoles.USER_ROLE_NUTZER,
            UserRoles.USER_ROLE_ADMIN,
            UserRoles.USER_ROLE_ACTUATOR,
        ]);
    }

    public checkRole(allowedRoles: UserRoles | UserRoles[]): boolean {
        return this.isRoleInPermissions(Array.isArray(allowedRoles) ? allowedRoles : [
            allowedRoles,
        ], this.user?.roles);
    }

    private isRoleInPermissions(allowedRoles: UserRoles[], userRoles: any): boolean {
        if (Array.isArray(userRoles)) {
            return allowedRoles.some(role => userRoles.includes(role));
        }
        return false;
    }

    public login() {
        window.location.href = environment.oauth.loginUrl;
    }

    public async logout() {
        await firstValueFrom(this.httpClient.post(this.logoutUrl, ''));
        this.clear();
        window.location.href = environment.oauth.logoutUrl;
    }

    public isAdmin(user: UflaUser) {
        return user && user.roles && user.roles.indexOf('TNB_UFLA_rolle_admin') > -1;
    }

    public isUser(user: UflaUser) {
        return user && user.roles && user.roles.indexOf('TNB_UFLA_rolle_nutzer') > -1;
    }

    public isUenb(user: UflaUser) {
        return user && user.organisationsTyp && user.organisationsTyp === OrganisationsTyp.UENB;
    }

    public isVnb(user: UflaUser) {
        return user && user.organisationsTyp && user.organisationsTyp === OrganisationsTyp.VNB;
    }

    public isVnbAdmin(user: UflaUser) {
        return this.isAdmin(user) && this.isVnb(user);
    }

    public isVnbObserver(user: UflaUser) {
        return user && (user.organisationsTyp && user.organisationsTyp === 'VNB') &&
      (user.roles && user.roles.indexOf('TNB_UFLA_rolle_nutzer') > -1);
    }
}
